// src/services/feeCollection.service.ts
import { 
  Connection, 
  PublicKey, 
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL
} from '@solana/web3.js';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';

// Export the class to make this a module
export class FeeCollectionService {
  private connection: Connection;
  
  // IMPORTANT: Set your platform wallet addresses here!
  private PLATFORM_WALLETS = {
    // Main treasury wallet - receives most fees
    TREASURY: 'HUNoMgxmK56woHg9VWVwKB3rnWUvHt3Hqy6FomNrDiWz', // <-- CHANGE THIS!
    
    // Team wallet - for team distribution
    TEAM: 'J95k17sj97hHAB3gydBgA1oBFhiY27nbA46LFFDwHgLQ', // <-- CHANGE THIS!
    
    // Development fund - for ongoing development
    DEVELOPMENT: 'A8Kq1yNBW6pJKyH5qXmGTWgbU5b3KoZd1YnF9ebapX83', // <-- CHANGE THIS!
  };

  // Fee distribution percentages
  private FEE_DISTRIBUTION = {
    TREASURY: 0.5,      // 50% to treasury
    TEAM: 0.3,         // 30% to team
    DEVELOPMENT: 0.15,  // 15% to development
    REFERRER: 0.05     // 5% to referrer (if any)
  };

  constructor() {
    this.connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed');
    this.validateWallets();
  }

  private validateWallets() {
    // Check if wallets are configured
    if (this.PLATFORM_WALLETS.TREASURY === 'YOUR_TREASURY_WALLET_ADDRESS_HERE') {
      console.error('⚠️ WARNING: Platform wallets not configured! Fees will not be collected.');
      console.error('Please update PLATFORM_WALLETS in feeCollection.service.ts');
    }
  }

  /**
   * Collect platform fee for token creation
   */
  async collectTokenCreationFee(
    fromWallet: PublicKey,
    referrer: string | null,
    signTransaction: (tx: Transaction) => Promise<Transaction>
  ): Promise<{
    success: boolean;
    signature?: string;
    error?: string;
  }> {
    try {
      const platformFee = 0.1; // 0.1 SOL platform fee
      const liquidityAmount = 1.0; // 1.0 SOL for liquidity
      const totalAmount = platformFee + liquidityAmount;

      // Create transaction
      const transaction = new Transaction();

      // Add platform fee transfers
      if (this.PLATFORM_WALLETS.TREASURY !== 'HUNoMgxmK56woHg9VWVwKB3rnWUvHt3Hqy6FomNrDiWz') {
        // Treasury gets 50% of platform fee
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: fromWallet,
            toPubkey: new PublicKey(this.PLATFORM_WALLETS.TREASURY),
            lamports: Math.floor(platformFee * 0.5 * LAMPORTS_PER_SOL),
          })
        );

        // Team gets 30% of platform fee
        if (this.PLATFORM_WALLETS.TEAM !== 'J95k17sj97hHAB3gydBgA1oBFhiY27nbA46LFFDwHgLQ') {
          transaction.add(
            SystemProgram.transfer({
              fromPubkey: fromWallet,
              toPubkey: new PublicKey(this.PLATFORM_WALLETS.TEAM),
              lamports: Math.floor(platformFee * 0.3 * LAMPORTS_PER_SOL),
            })
          );
        }

        // Development gets 15% of platform fee
        if (this.PLATFORM_WALLETS.DEVELOPMENT !== 'A8Kq1yNBW6pJKyH5qXmGTWgbU5b3KoZd1YnF9ebapX83') {
          transaction.add(
            SystemProgram.transfer({
              fromPubkey: fromWallet,
              toPubkey: new PublicKey(this.PLATFORM_WALLETS.DEVELOPMENT),
              lamports: Math.floor(platformFee * 0.15 * LAMPORTS_PER_SOL),
            })
          );
        }

        // Referrer gets 5% if exists
        if (referrer && this.isValidPublicKey(referrer)) {
          transaction.add(
            SystemProgram.transfer({
              fromPubkey: fromWallet,
              toPubkey: new PublicKey(referrer),
              lamports: Math.floor(platformFee * 0.05 * LAMPORTS_PER_SOL),
            })
          );
        }
      }

      // Get recent blockhash
      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromWallet;

      // Sign transaction
      const signedTx = await signTransaction(transaction);
      
      // Send transaction
      const signature = await this.connection.sendRawTransaction(signedTx.serialize());
      
      // Confirm transaction
      await this.connection.confirmTransaction(signature);

      console.log('✅ Platform fee collected:', signature);
      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ Failed to collect fee:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Collect bundler deployment fee
   */
  async collectBundlerFee(
    fromWallet: PublicKey,
    walletCount: number,
    stealthMode: boolean,
    referrer: string | null,
    signTransaction: (tx: Transaction) => Promise<Transaction>
  ): Promise<{
    success: boolean;
    signature?: string;
    error?: string;
  }> {
    try {
      const baseFee = 0.5; // Base bundler fee
      const stealthFee = stealthMode ? 0.05 : 0; // Stealth mode fee
      const totalFee = baseFee + stealthFee;

      const transaction = new Transaction();

      if (this.PLATFORM_WALLETS.TREASURY !== 'YOUR_TREASURY_WALLET_ADDRESS_HERE') {
        // Distribute fees according to percentages
        const treasuryAmount = totalFee * this.FEE_DISTRIBUTION.TREASURY;
        const teamAmount = totalFee * this.FEE_DISTRIBUTION.TEAM;
        const devAmount = totalFee * this.FEE_DISTRIBUTION.DEVELOPMENT;
        const referrerAmount = referrer ? totalFee * this.FEE_DISTRIBUTION.REFERRER : 0;

        // Add transfers
        transaction.add(
          SystemProgram.transfer({
            fromPubkey: fromWallet,
            toPubkey: new PublicKey(this.PLATFORM_WALLETS.TREASURY),
            lamports: Math.floor(treasuryAmount * LAMPORTS_PER_SOL),
          })
        );

        if (this.PLATFORM_WALLETS.TEAM !== 'YOUR_TEAM_WALLET_ADDRESS_HERE') {
          transaction.add(
            SystemProgram.transfer({
              fromPubkey: fromWallet,
              toPubkey: new PublicKey(this.PLATFORM_WALLETS.TEAM),
              lamports: Math.floor(teamAmount * LAMPORTS_PER_SOL),
            })
          );
        }

        if (this.PLATFORM_WALLETS.DEVELOPMENT !== 'YOUR_DEV_WALLET_ADDRESS_HERE') {
          transaction.add(
            SystemProgram.transfer({
              fromPubkey: fromWallet,
              toPubkey: new PublicKey(this.PLATFORM_WALLETS.DEVELOPMENT),
              lamports: Math.floor(devAmount * LAMPORTS_PER_SOL),
            })
          );
        }

        if (referrer && this.isValidPublicKey(referrer)) {
          transaction.add(
            SystemProgram.transfer({
              fromPubkey: fromWallet,
              toPubkey: new PublicKey(referrer),
              lamports: Math.floor(referrerAmount * LAMPORTS_PER_SOL),
            })
          );
        }
      }

      // Get recent blockhash
      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromWallet;

      // Sign and send
      const signedTx = await signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTx.serialize());
      await this.connection.confirmTransaction(signature);

      console.log('✅ Bundler fee collected:', signature);
      return {
        success: true,
        signature
      };

    } catch (error: any) {
      console.error('❌ Failed to collect bundler fee:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get platform wallet balances
   */
  async getPlatformBalances(): Promise<{
    treasury: number;
    team: number;
    development: number;
    total: number;
  }> {
    try {
      let treasuryBalance = 0;
      let teamBalance = 0;
      let developmentBalance = 0;

      if (this.PLATFORM_WALLETS.TREASURY !== 'YOUR_TREASURY_WALLET_ADDRESS_HERE') {
        treasuryBalance = await this.connection.getBalance(
          new PublicKey(this.PLATFORM_WALLETS.TREASURY)
        ) / LAMPORTS_PER_SOL;
      }

      if (this.PLATFORM_WALLETS.TEAM !== 'YOUR_TEAM_WALLET_ADDRESS_HERE') {
        teamBalance = await this.connection.getBalance(
          new PublicKey(this.PLATFORM_WALLETS.TEAM)
        ) / LAMPORTS_PER_SOL;
      }

      if (this.PLATFORM_WALLETS.DEVELOPMENT !== 'YOUR_DEV_WALLET_ADDRESS_HERE') {
        developmentBalance = await this.connection.getBalance(
          new PublicKey(this.PLATFORM_WALLETS.DEVELOPMENT)
        ) / LAMPORTS_PER_SOL;
      }

      return {
        treasury: treasuryBalance,
        team: teamBalance,
        development: developmentBalance,
        total: treasuryBalance + teamBalance + developmentBalance
      };

    } catch (error) {
      console.error('Failed to get platform balances:', error);
      return {
        treasury: 0,
        team: 0,
        development: 0,
        total: 0
      };
    }
  }

  /**
   * Check if platform wallets are configured
   */
  isConfigured(): boolean {
    return this.PLATFORM_WALLETS.TREASURY !== 'YOUR_TREASURY_WALLET_ADDRESS_HERE';
  }

  /**
   * Get platform wallet addresses
   */
  getPlatformWallets() {
    return {
      treasury: this.PLATFORM_WALLETS.TREASURY,
      team: this.PLATFORM_WALLETS.TEAM,
      development: this.PLATFORM_WALLETS.DEVELOPMENT,
      isConfigured: this.isConfigured()
    };
  }

  /**
   * Update platform wallets (call this from an admin panel)
   */
  updatePlatformWallets(wallets: {
    treasury?: string;
    team?: string;
    development?: string;
  }) {
    if (wallets.treasury && this.isValidPublicKey(wallets.treasury)) {
      this.PLATFORM_WALLETS.TREASURY = wallets.treasury;
    }
    if (wallets.team && this.isValidPublicKey(wallets.team)) {
      this.PLATFORM_WALLETS.TEAM = wallets.team;
    }
    if (wallets.development && this.isValidPublicKey(wallets.development)) {
      this.PLATFORM_WALLETS.DEVELOPMENT = wallets.development;
    }

    // Save to localStorage for persistence
    localStorage.setItem('platform_wallets', JSON.stringify(this.PLATFORM_WALLETS));
    console.log('✅ Platform wallets updated');
  }

  /**
   * Load saved platform wallets from localStorage
   */
  loadSavedWallets() {
    const saved = localStorage.getItem('platform_wallets');
    if (saved) {
      try {
        const wallets = JSON.parse(saved);
        this.PLATFORM_WALLETS = { ...this.PLATFORM_WALLETS, ...wallets };
        console.log('✅ Loaded saved platform wallets');
      } catch (error) {
        console.error('Failed to load saved wallets:', error);
      }
    }
  }

  /**
   * Validate Solana public key
   */
  private isValidPublicKey(key: string): boolean {
    try {
      new PublicKey(key);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get fee breakdown for display
   */
  getFeeBreakdown(feeAmount: number) {
    return {
      treasury: feeAmount * this.FEE_DISTRIBUTION.TREASURY,
      team: feeAmount * this.FEE_DISTRIBUTION.TEAM,
      development: feeAmount * this.FEE_DISTRIBUTION.DEVELOPMENT,
      referrer: feeAmount * this.FEE_DISTRIBUTION.REFERRER,
      total: feeAmount
    };
  }
}

// Create and export a singleton instance
const feeCollectionService = new FeeCollectionService();
export default feeCollectionService;